import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

interface Achievement {
  id: string;
  code: string;
  name: string;
  description: string;
  icon: string;
  points: number;
  category: string;
  is_secret: boolean;
  requirement_type: string;
  requirement_value: number | null;
}

interface UserAchievement {
  user_id: string;
  achievement_id: string;
  unlocked_at: string;
  achievement?: Achievement;
}

interface AchievementUnlock {
  achievement: Achievement;
  isNew: boolean;
}

export const useAchievements = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [recentUnlock, setRecentUnlock] = useState<Achievement | null>(null);

  const fetchAchievements = useCallback(async () => {
    // Fetch all achievements
    const { data: allAchievements, error: achievementsError } = await supabase
      .from('achievements')
      .select('*')
      .order('category', { ascending: true });

    if (achievementsError) {
      console.error('Error fetching achievements:', achievementsError);
    } else {
      setAchievements(allAchievements as Achievement[]);
    }

    // Fetch user's unlocked achievements
    if (user) {
      const { data: userAch, error: userAchError } = await supabase
        .from('user_achievements')
        .select('*, achievement:achievements(*)')
        .eq('user_id', user.id);

      if (userAchError) {
        console.error('Error fetching user achievements:', userAchError);
      } else {
        setUserAchievements(userAch as UserAchievement[]);
      }
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  const isUnlocked = useCallback((achievementCode: string): boolean => {
    const achievement = achievements.find(a => a.code === achievementCode);
    if (!achievement) return false;
    return userAchievements.some(ua => ua.achievement_id === achievement.id);
  }, [achievements, userAchievements]);

  const unlockAchievement = useCallback(async (code: string): Promise<AchievementUnlock | null> => {
    if (!user) return null;

    const achievement = achievements.find(a => a.code === code);
    if (!achievement) {
      console.error('Achievement not found:', code);
      return null;
    }

    // Check if already unlocked
    if (isUnlocked(code)) {
      return { achievement, isNew: false };
    }

    // Insert user achievement
    const { error } = await supabase
      .from('user_achievements')
      .insert({
        user_id: user.id,
        achievement_id: achievement.id,
      });

    if (error) {
      if (error.code === '23505') { // Unique constraint violation - already exists
        return { achievement, isNew: false };
      }
      console.error('Error unlocking achievement:', error);
      return null;
    }

    // Show toast notification
    toast({
      title: `🏆 Achievement Unlocked!`,
      description: `${achievement.name}: ${achievement.description}`,
    });

    setRecentUnlock(achievement);
    setTimeout(() => setRecentUnlock(null), 5000);

    // Add points to profile
    if (profile) {
      await supabase
        .from('profiles')
        .update({ aura_points: (profile.aura_points || 0) + achievement.points })
        .eq('id', user.id);
    }

    await fetchAchievements();
    return { achievement, isNew: true };
  }, [user, profile, achievements, isUnlocked, toast, fetchAchievements]);

  const checkStreakAchievements = useCallback(async (streakDays: number) => {
    const streakAchievements = [
      { code: 'streak_3', days: 3 },
      { code: 'streak_7', days: 7 },
      { code: 'streak_14', days: 14 },
      { code: 'streak_30', days: 30 },
      { code: 'streak_100', days: 100 },
      { code: 'streak_365', days: 365 },
    ];

    for (const sa of streakAchievements) {
      if (streakDays >= sa.days && !isUnlocked(sa.code)) {
        await unlockAchievement(sa.code);
      }
    }
  }, [isUnlocked, unlockAchievement]);

  const checkClosetAchievements = useCallback(async (itemCount: number) => {
    const closetAchievements = [
      { code: 'first_item', count: 1 },
      { code: 'closet_10', count: 10 },
      { code: 'closet_25', count: 25 },
      { code: 'closet_50', count: 50 },
      { code: 'closet_100', count: 100 },
    ];

    for (const ca of closetAchievements) {
      if (itemCount >= ca.count && !isUnlocked(ca.code)) {
        await unlockAchievement(ca.code);
      }
    }
  }, [isUnlocked, unlockAchievement]);

  const checkLikeAchievements = useCallback(async (likeCount: number) => {
    if (likeCount >= 1 && !isUnlocked('first_like')) {
      await unlockAchievement('first_like');
    }
    if (likeCount >= 10 && !isUnlocked('likes_10')) {
      await unlockAchievement('likes_10');
    }
    if (likeCount >= 50 && !isUnlocked('likes_50')) {
      await unlockAchievement('likes_50');
    }
  }, [isUnlocked, unlockAchievement]);

  const checkSecretAchievements = useCallback(async () => {
    const now = new Date();
    const hour = now.getHours();

    // Night owl - after midnight, before 5am
    if (hour >= 0 && hour < 5 && !isUnlocked('night_owl')) {
      await unlockAchievement('night_owl');
    }

    // Early bird - before 6am
    if (hour >= 5 && hour < 6 && !isUnlocked('early_bird')) {
      await unlockAchievement('early_bird');
    }
  }, [isUnlocked, unlockAchievement]);

  const getProgress = useCallback(() => {
    const totalVisible = achievements.filter(a => !a.is_secret).length;
    const unlockedVisible = userAchievements.filter(ua => {
      const ach = achievements.find(a => a.id === ua.achievement_id);
      return ach && !ach.is_secret;
    }).length;

    return {
      unlocked: userAchievements.length,
      total: achievements.length,
      unlockedVisible,
      totalVisible,
      percentage: totalVisible > 0 ? Math.round((unlockedVisible / totalVisible) * 100) : 0,
    };
  }, [achievements, userAchievements]);

  const getByCategory = useCallback((category: string) => {
    return achievements
      .filter(a => a.category === category)
      .map(a => ({
        ...a,
        unlocked: isUnlocked(a.code),
        unlockedAt: userAchievements.find(ua => ua.achievement_id === a.id)?.unlocked_at,
      }));
  }, [achievements, userAchievements, isUnlocked]);

  const getTotalPoints = useCallback(() => {
    return userAchievements.reduce((total, ua) => {
      const achievement = achievements.find(a => a.id === ua.achievement_id);
      return total + (achievement?.points || 0);
    }, 0);
  }, [achievements, userAchievements]);

  return {
    achievements,
    userAchievements,
    loading,
    recentUnlock,
    isUnlocked,
    unlockAchievement,
    checkStreakAchievements,
    checkClosetAchievements,
    checkLikeAchievements,
    checkSecretAchievements,
    getProgress,
    getByCategory,
    getTotalPoints,
    refresh: fetchAchievements,
  };
};
